import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface IEventoTimeline {
  id?: number;
  fecha: string;
  nombre: string;
  descripcion: string;
  note_id?: number;
  lab_result_id?: number;
  metadata?: any;
  paraclinico?: {
    id: string;
    backendId?: number;
    nombre: string;
    tipo: 'interno' | 'laboratorio' | 'gabinete';
    fecha: string;
    pdfUrl?: string;
  };
  created_by?: {
    id: number;
    name: string;
  };
}

export interface IHistoricoTimeline {
  titulo: string;
  eventos: IEventoTimeline[];
}

export interface TimelineRequest {
  patient_id: number;
  category: string;
  event_date: string;
  event_name: string;
  description?: string;
  medical_record_id?: number;
  lab_result_id?: number;
  metadata?: any;
}

@Injectable({ providedIn: 'root' })
export class PatientTimelineService {
  private apiUrl = `${environment.apiUrl}`;

  constructor(private http: HttpClient) {}

  /**
   * Obtiene el timeline completo de un paciente (agrupado por categoría)
   */
  getTimeline(patientId: number): Observable<IHistoricoTimeline[]> {
    return this.http.get<any>(`${this.apiUrl}/pacientes/${patientId}/timeline`)
      .pipe(
        map(response => response.data || []),
        catchError(this.handleError)
      );
  }

  /**
   * Obtiene las categorías disponibles del timeline de un paciente
   */
  getCategories(patientId: number): Observable<string[]> {
    return this.http.get<any>(`${this.apiUrl}/pacientes/${patientId}/timeline/categories`)
      .pipe(
        map(response => response.data || []),
        catchError(this.handleError)
      );
  }

  /**
   * Crea un nuevo evento en el timeline
   */
  createEvent(data: TimelineRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/timeline`, data)
      .pipe(
        map(response => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Actualiza un evento del timeline
   */
  updateEvent(id: number, data: TimelineRequest): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/timeline/${id}`, data)
      .pipe(
        map(response => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Elimina un evento del timeline
   */
  deleteEvent(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/timeline/${id}`)
      .pipe(
        map(response => response),
        catchError(this.handleError)
      );
  }

  /**
   * Manejo centralizado de errores
   */
  private handleError(error: any): Observable<never> {
    let errorMessage = 'Ocurrió un error en el servidor';

    // 422 = Validation Error (Laravel)
    if (error.status === 422 && error.error?.errors) {
      console.error('[PatientTimelineService] ❌ ERRORES DE VALIDACIÓN:');
      console.error('Full error object:', error);
      console.error('Validation errors:', error.error.errors);

      // Formatear errores de validación de Laravel
      const validationErrors = Object.entries(error.error.errors)
        .map(([field, messages]: [string, any]) => {
          const errorList = Array.isArray(messages) ? messages.join(', ') : messages;
          return `  • ${field}: ${errorList}`;
        })
        .join('\n');

      errorMessage = `Errores de validación:\n${validationErrors}`;
      console.error(errorMessage);
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 0) {
      errorMessage = 'No se pudo conectar con el servidor';
    } else if (error.status === 401) {
      errorMessage = 'Sesión expirada, por favor inicia sesión nuevamente';
    } else if (error.status === 404) {
      errorMessage = 'Recurso no encontrado';
    }

    console.error('[PatientTimelineService] Error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }
}
